/**
 * Search intent tracker — Phase F.2 (spec § 2.1 FR-201–FR-215).
 *
 * Observes raw aro_search events fired via trackAroSearchEvent, accumulates
 * their event_uuids into a client-side "in-progress intent" state persisted
 * to sessionStorage (survives cross-page nav dropdown → /search → PDP click),
 * detects terminal actions (click / new_search / abandoned), and fires ONE
 * consolidated search_intent event per intent with the timeline of raw
 * event_uuids. Dashboard reads intents directly; drill-down JOINs via the
 * uuid array back to search_events.
 *
 * Integration seam (spec § 4.1 Phase 2 Option C): native-search-api.js's
 * trackAroSearchEvent calls window.AroIntentTracker.observe(envelope)
 * synchronously after pushing to dataLayer. This tracker never sends to
 * the ingest webhook directly — the terminal search_intent event is fired
 * back through trackAroSearchEvent (which handles rate-limit + sendBeacon),
 * with a recursion guard on aro_event_type === 'search_intent'.
 *
 * State schema in sessionStorage.aro_current_intent (JSON blob):
 * {
 *   intent_uuid: string,               // crypto.randomUUID
 *   started_at: string,                // London ISO of first raw event
 *   first_keystroke_at: string|null,   // London ISO of first dropdown_query
 *   search_page_visited_at: string|null, // London ISO of search_page_view
 *   current_term: string|null,         // last known search_term
 *   active_tab: string|null,           // products / blogs / recipes
 *   event_uuids: string[],             // in occurred_at order
 *   dropdown_seen: bool,
 *   search_page_visited: bool,
 *   engaged: bool,                     // true if any event other than dropdown_opened has fired
 *   hidden_since: number|null          // Date.now() when tab went hidden
 * }
 *
 * All fires guarded with try/catch — analytics failure must never break UX.
 */
(function () {
  'use strict';

  var STATE_KEY = 'aro_current_intent';
  var INACTIVITY_MS = 5 * 60 * 1000;    // 5 minutes per DEC-007
  var HIT_ENTER_THRESHOLD_MS = 5000;    // per FR-208
  var inactivityTimerId = null;

  // ── state I/O ────────────────────────────────────────────────────────────
  function readState() {
    try {
      var raw = sessionStorage.getItem(STATE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }
  function writeState(s) {
    try {
      if (s === null) sessionStorage.removeItem(STATE_KEY);
      else sessionStorage.setItem(STATE_KEY, JSON.stringify(s));
    } catch (e) { /* sessionStorage quota / private mode — silent */ }
  }

  function newIntentUuid() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
    } catch (e) {}
    return 'aro-int-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
  }

  // ── FR-210: materially-different-term via prefix rule ────────────────────
  function isSameSearchIntent(oldTerm, newTerm) {
    if (!oldTerm && !newTerm) return true;
    if (!oldTerm || !newTerm) return true;   // one absent → treat as continuation (e.g. dropdown_opened before typing)
    var a = String(oldTerm).trim().toLowerCase();
    var b = String(newTerm).trim().toLowerCase();
    if (a === b) return true;
    // prefix either way: `vit` → `vitamin` (extension) OR `vitamin` → `vit` (heavy backspace)
    return a.indexOf(b) === 0 || b.indexOf(a) === 0;
  }

  function resetInactivityTimer() {
    if (inactivityTimerId) { clearTimeout(inactivityTimerId); }
    inactivityTimerId = setTimeout(function () {
      fireTerminal('abandoned', null, null);
    }, INACTIVITY_MS);
  }

  function clearInactivityTimer() {
    if (inactivityTimerId) { clearTimeout(inactivityTimerId); inactivityTimerId = null; }
  }

  // ── intent lifecycle ─────────────────────────────────────────────────────
  function startIntent(envelope) {
    var state = {
      intent_uuid: newIntentUuid(),
      started_at: envelope.occurred_at,
      first_keystroke_at: null,
      search_page_visited_at: null,
      current_term: null,
      active_tab: null,
      event_uuids: [],
      dropdown_seen: false,
      search_page_visited: false,
      engaged: false,
      hidden_since: null
    };
    writeState(state);
    resetInactivityTimer();
    return state;
  }

  function fireTerminal(terminalAction, terminalSource, terminalTarget) {
    var state = readState();
    // FR-214: zero-event intents silently drop (nothing to summarise)
    if (!state || !state.event_uuids || state.event_uuids.length === 0) {
      writeState(null);
      clearInactivityTimer();
      return;
    }

    // Silent-drop `abandoned` intents with no user engagement (spec § 2.1
    // refinement 2026-08-14): focus-only intents where the user opened the
    // dropdown but never typed, tab-switched, clicked, or landed on /search
    // carry too little signal to warrant an intent row. The raw
    // dropdown_opened event still lands in search_events for aggregate
    // "focus count" reports.
    // Backwards-compat: infer engaged for sessionStorage state written by an
    // older tracker version pre-deploy.
    var engaged = state.engaged;
    if (engaged === undefined) {
      engaged = !!(state.current_term || state.search_page_visited || state.event_uuids.length > 1);
    }
    if (terminalAction === 'abandoned' && !engaged) {
      writeState(null);
      clearInactivityTimer();
      return;
    }

    var timeFromKeystrokeMs = null;
    var hitEnter = null;
    if (state.first_keystroke_at && state.search_page_visited_at) {
      try {
        var t1 = new Date(state.first_keystroke_at).getTime();
        var t2 = new Date(state.search_page_visited_at).getTime();
        if (!isNaN(t1) && !isNaN(t2)) {
          timeFromKeystrokeMs = t2 - t1;
          hitEnter = timeFromKeystrokeMs <= HIT_ENTER_THRESHOLD_MS;
        }
      } catch (e) {}
    }

    var payload = {
      intent_uuid: state.intent_uuid,
      started_at: state.started_at,
      ended_at: new Date().toISOString(),   // trackAroSearchEvent adds its own London-local occurred_at; this is intent-specific "ended" moment
      final_search_term: state.current_term,
      active_tab_at_end: state.active_tab,
      terminal_action: terminalAction,
      terminal_source: terminalSource,
      terminal_target: terminalTarget,
      hit_enter_under_threshold: hitEnter,
      time_from_first_keystroke_to_enter_ms: timeFromKeystrokeMs,
      dropdown_seen: state.dropdown_seen,
      search_page_visited: state.search_page_visited,
      event_uuids: state.event_uuids
    };

    // Fire the terminal event via the same trackAroSearchEvent pathway.
    // Recursion is bounded — observe() skips events of type 'search_intent'.
    try {
      if (window.AroSearchAPI && typeof window.AroSearchAPI.trackAroSearchEvent === 'function') {
        window.AroSearchAPI.trackAroSearchEvent('search_intent', payload);
      }
    } catch (e) { /* analytics never blocks UX */ }

    writeState(null);
    clearInactivityTimer();
  }

  // Helper: append + persist without side-effects (used by terminal-including-current-event flows)
  function appendAndPersist(state, envelope) {
    state.event_uuids.push(envelope.event_uuid);
    writeState(state);
  }

  // ── FR-201 through FR-214: the observation reducer ───────────────────────
  function observe(envelope) {
    if (!envelope) return;
    var eventType = envelope.aro_event_type;
    // Recursion guard — our own terminal fire re-enters here; ignore it.
    if (eventType === 'search_intent') return;
    // Scope guard — intent tracker only cares about search-flow events
    // (dropdown_* + search_*). native-search-api.js pipes EVERY tracked
    // event through observe(); without this guard, product_view (from
    // aro-pdp-analytics.js) and any future non-search events would seed
    // phantom intents that abandon 5min later, polluting search_intents
    // with zero-term rows. Discovered 2026-08-14 on live-data audit.
    if (typeof eventType !== 'string') return;
    if (eventType.indexOf('dropdown_') !== 0 && eventType.indexOf('search_') !== 0) return;
    // Guard: need event_uuid to accumulate
    if (!envelope.event_uuid) return;

    var payload = envelope.payload || {};
    var state = readState();

    // Terminal click events (FR-206): include the click in the timeline, then fire.
    // Ensure state exists first (edge: click without prior state — synthesise an intent so the click still surfaces).
    if (
      eventType === 'dropdown_product_click' ||
      eventType === 'dropdown_article_click' ||
      eventType === 'search_product_click' ||
      eventType === 'search_article_click'
    ) {
      if (!state) state = startIntent(envelope);
      state.engaged = true;   // clicks are always engagement
      appendAndPersist(state, envelope);

      var source, target;
      if (eventType.indexOf('dropdown_') === 0) source = 'dropdown';
      else source = 'search_page';

      if (eventType.indexOf('_product_click') !== -1) {
        target = payload.product_handle || null;
      } else {
        target = payload.article_url || null;
      }

      var action = eventType.indexOf('_product_click') !== -1 ? 'product_click' : 'article_click';
      fireTerminal(action, source, target);
      return;
    }

    // dropdown_query: check for materially-different-term (new_search terminal).
    // The current dropdown_query event belongs to the NEW intent (it triggered the swap),
    // so terminate FIRST (without including current), then start fresh + append.
    if (eventType === 'dropdown_query') {
      var newTerm = payload.search_term || null;
      if (state && state.current_term && !isSameSearchIntent(state.current_term, newTerm)) {
        fireTerminal('new_search', null, null);
        state = null;   // fireTerminal cleared state
      }
    }

    // No active intent → start one (FR-204).
    if (!state) {
      state = startIntent(envelope);
    }

    // Append the current event's uuid to the intent's timeline (FR-205).
    state.event_uuids.push(envelope.event_uuid);

    // Update flags based on event type.
    if (
      eventType === 'dropdown_opened' || eventType === 'dropdown_query' ||
      eventType === 'dropdown_suggestion_click' || eventType === 'dropdown_tab_switch' ||
      eventType === 'dropdown_view_all_click' || eventType === 'dropdown_filter_toggle'
    ) {
      state.dropdown_seen = true;
    }
    if (eventType === 'search_page_view') {
      state.search_page_visited = true;
      state.search_page_visited_at = envelope.occurred_at;
      state.current_term = payload.search_term || state.current_term;
    }
    if (eventType === 'dropdown_query') {
      if (!state.first_keystroke_at) state.first_keystroke_at = envelope.occurred_at;
      state.current_term = payload.search_term || state.current_term;
    }
    if (eventType === 'dropdown_tab_switch' || eventType === 'search_tab_switch') {
      state.active_tab = payload.to_tab || state.active_tab;
    }
    if (eventType !== 'dropdown_opened') {
      state.engaged = true;
    }

    writeState(state);
    resetInactivityTimer();
  }

  // ── FR-215: abandoned on pagehide + visibilitychange 5-min ───────────────
  window.addEventListener('pagehide', function () {
    var state = readState();
    if (state && state.event_uuids && state.event_uuids.length > 0) {
      fireTerminal('abandoned', null, null);
    }
  });

  document.addEventListener('visibilitychange', function () {
    var state = readState();
    if (!state) return;
    if (document.visibilityState === 'hidden') {
      state.hidden_since = Date.now();
      writeState(state);
    } else if (document.visibilityState === 'visible') {
      if (state.hidden_since) {
        var elapsed = Date.now() - state.hidden_since;
        state.hidden_since = null;
        writeState(state);
        if (elapsed >= INACTIVITY_MS) {
          fireTerminal('abandoned', null, null);
        } else {
          resetInactivityTimer();   // resume timer with fresh window
        }
      }
    }
  });

  // ── Public API ───────────────────────────────────────────────────────────
  window.AroIntentTracker = {
    observe: observe
  };
})();
